/**
 * `/invite/:code` 向けに入力を正規化する。
 * - 前後空白のみ除去（大文字小文字は変換しない）
 * - ルート param の生 8 文字、または貼られた /invite/{code} URL（防御的）
 */

/** 発行側と同じ曖昧文字除外アルファベット（照合用の長さチェックのみ）。 */
const INVITE_CODE_RE = /^[23456789abcdefghjkmnpqrstuvwxyz]{8}$/;

export type ParseInviteInputResult =
  | { ok: true; code: string }
  | { ok: false; reason: 'empty' | 'invalid' };

/**
 * 招待コードまたは招待 URL を解釈する。
 * @returns 成功時は厳密 8 文字の code
 */
export function parseInviteInput(raw: string): ParseInviteInputResult {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { ok: false, reason: 'empty' };
  }

  const fromUrl = extractCodeFromInviteUrl(trimmed);
  if (fromUrl !== null) {
    return INVITE_CODE_RE.test(fromUrl)
      ? { ok: true, code: fromUrl }
      : { ok: false, reason: 'invalid' };
  }

  if (INVITE_CODE_RE.test(trimmed)) {
    return { ok: true, code: trimmed };
  }

  return { ok: false, reason: 'invalid' };
}

/**
 * 絶対／相対の招待 URL から code を取り出す。該当しなければ null。
 */
function extractCodeFromInviteUrl(input: string): string | null {
  // パスだけ、または絶対 URL
  let pathname: string;
  try {
    if (/^https?:\/\//i.test(input)) {
      pathname = new URL(input).pathname;
    } else if (input.startsWith('/')) {
      pathname = input.split('?')[0]?.split('#')[0] ?? input;
    } else {
      return null;
    }
  } catch {
    return null;
  }

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < 2 || segments[0] !== 'invite') {
    return null;
  }

  // /invite/{code}（余分な末尾セグメントは無効）
  if (segments.length !== 2) {
    return null;
  }

  return segments[1] ?? null;
}
